import { withAuthStore } from 'context/AuthStore'
import { useResErrors, useStateCallback } from 'lib/hooks'
import { API_SIGN_DOCS, ApiDigital } from 'lib/api'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from '@material-ui/core'
import { useParams } from 'react-router'
import { useSnackbar } from 'notistack'
import { closeSSE, initSSE, subscribeToSSE } from '../../lib/SSE/service/sseService'
import { CookieService } from '../../lib/utils'
import { DOCUMENT_PATH } from '../../routes/constants.routes'

export const HomeStoreContext = createContext({
  homeStore: {},
  homeStoreFunc: {}
})

const paginationInitalState = {
  limit: 10,
  offset: 0,
  count: 0
}

const statusMap = {
  at_signing: 'at_signing',
  signed: 'signed',
  new: 'new',
  refused: 'refused'
}

const HomeStoreProvider = props => {
  const { id } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { standardCheck } = useResErrors()
  const [lists, setLists] = useState([])
  const [document, setDocument] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selectedTab, setSelectedTab] = useStateCallback('new')
  const [pagination, setPagination] = useState(paginationInitalState)
  const [isMobile, setIsMobile] = useState(false)

  const status = statusMap[selectedTab]




  const checkIsMobile = () => {
    if (window.innerWidth < 756) {
      setIsMobile(true)
    } else {
      setIsMobile(false)
    }
  }

  useEffect(() => {
    getDocumentList(status, pagination.limit, pagination.offset)
  }, [selectedTab])

  useEffect(() => {
    if (id) {
      getById(id)
    }
  }, [id])

  useEffect(() => {
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)

    return () => {
      window.removeEventListener('resize', checkIsMobile)
    }
  }, [])

  const handlePaginationChange = async (e, val) => {
    if (status) {
      const res = await getDocumentList(status, pagination.limit, val)
      if (res && res.data) {
        setPagination(prevState => ({
          ...prevState,
          offset: val,
          count: res.data.totalPages
        }))
      }
    }
  }

  const getById = useCallback(
    async id => {
      try {
        setLoading(true)
        const res = await ApiDigital.getContractById(id)
        await standardCheck(
          res,
          ({ data }) => {
            setDocument(data)
          },
          {
            onFail: () => {
              setDocument([])
            }
          }
        )
      } catch (error) {
        console.error('Ошибка', error)
        setLists([])
      } finally {
        setLoading(false)
      }
    },
    [standardCheck]
  )

  const postContractId = async id => {
    try {
      setLoading(true)
      const res = await ApiDigital.postContractId(id)
      await standardCheck(res, () => {
        if (res?.data) {
          enqueueSnackbar('Успешно', 'info')
        }
      })
    } catch (error) {
      console.error('Ошибка', error)
    } finally {
      setLoading(false)
    }
  }

  const sendSms = async id => {
    try {
      setLoading(true)
      const res = await ApiDigital.sendSmsContracts(id)
      await standardCheck(res, () => {
        if (res?.data) {
          enqueueSnackbar('Успешно', 'info')
        }
      },
        {
        onErrCustom: () => {
          enqueueSnackbar(res?.msg, { variant: 'error' })
        }
      })
    } catch (error) {
      console.error('Ошибка', error)
      enqueueSnackbar(`${error.message}`, error)
    } finally {
      setLoading(false)
    }
  }

  const getQrBySignerId = async id => {
    try {
      setLoading(true)
      return await ApiDigital.getQrBySignerId(id)
    } catch (error) {
      console.error('Ошибка', error)
    } finally {
      setLoading(false)
    }
  }

  const getDocumentList = async (status, limit, offset) => {
    setLoading(true)
    const data = {
      offset: offset > 1 ? (offset - 1) * 10 : 0,
      limit,
      ...(status === 'new'
        ? { statusCodes: ['new'] }
        : status === 'at_signing'
        ? { statusCodes: ['accessed', 'processing'] }
        : status === 'signed'
        ? { statusCodes: ['signed', 'exported'] }
        : status === 'refused'
        ? { statusCodes: ['refused'] }
        : { statusCode: status })
    }

    try {
      const res = await ApiDigital.getDocumentList(data)
      await standardCheck(
        res,
        ({ data }) => {
          setPagination(prevState => ({
            ...prevState,
            offset,
            count: res?.data?.totalPages
          }))
          if (data.content) {
            setLists(data.content)
          } else {
            setLists([])
          }
        },
        {
          onFail: () => {
            setLists([])
          }
        }
      )
      return res
    } catch (error) {
      console.error('Ошибка', error)
      setLists([])
      return null
    } finally {
      setLoading(false)
    }
  }



  const onChangeTab = (event, newValue) => {
    setPagination(paginationInitalState)
    setSelectedTab(newValue, s => {})
  }

  const deleteDocument = async id => {
    try {
      setLoading(true)
      return await ApiDigital.deleteDocument(id)
    } catch (error) {
      console.error('Ошибка', error)
    } finally {
      setLoading(false)
    }
  }

  const checkAndRedirectToContract = async router => {
    const contractId = CookieService.getCookie('redirectToContractId')
    if (!contractId) return false

    try {
      setLoading(true)
      const res = await ApiDigital.getContractById(contractId)

      if ((res?.status === 200 || res?.status === 201) && res?.data) {
        CookieService.deleteCookieEverywhere('redirectToContractId')
        router.goTo(`${DOCUMENT_PATH}/${contractId}`)
        return true
      } else {
        enqueueSnackbar(`${res.msg}`, { variant: 'error' })
      }
    } catch (error) {
      console.error('Ошибка при проверке контракта для редиректа:', error)
    } finally {
      CookieService.deleteCookieEverywhere('redirectToContractId')
      setLoading(false)
    }

    return false
  }

  const getDocumentLink = async (docId) => {
    setLoading(true)
    try {
      const res = await ApiDigital.getDocumentLink(docId)
      return res
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || error.message || 'Не удалось получить ссылку',
        { variant: 'error' }
      )
      return null
    } finally {
      setLoading(false)
    }
  }

  const debouncedReload = useMemo(() => {
    return debounce(() => getDocumentList(status, pagination.limit, pagination.offset), 300)
  }, [status, pagination.limit, pagination.offset])

  const getResignCount = async (id) => {
    setLoading(true)
    try {
      const res = await ApiDigital.getResignCount(id)
      return res
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || error.message || 'Не удалось получить количество попыток подписания',
        { variant: 'error' }
      )
      return null
    } finally {
      setLoading(false)
    }
  }



  useEffect(() => {
    const unsubscribe = subscribeToSSE(async payload => {
      const currentContractId = id?.toString()
      const incomingId = `${payload}`
      const lastVisit = sessionStorage.getItem('last_visit')

      const isOnContractPage = lastVisit?.startsWith('/document/')
      const isOnHomePage = lastVisit === '/' || lastVisit === '/home'

      if (isOnContractPage && currentContractId === incomingId) {
        await getById(payload)
        await debouncedReload()
      }

      if (isOnHomePage) {
        await debouncedReload()
      }
    })

    return () => unsubscribe()
  }, [id, debouncedReload, getById])


  useEffect(() => {
    const clientId = localStorage.getItem('sseClientId') || (() => {
      const id = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`
      localStorage.setItem('sseClientId', id)
      return id
    })()

    initSSE(`${process.env.REACT_APP_API_SIGN_DOCS || API_SIGN_DOCS}/sse/subscribe?clientId=${clientId}`)

    const cleanup = () => closeSSE()
    window.addEventListener('beforeunload', cleanup)
    return () => window.removeEventListener('beforeunload', cleanup)
  }, [])

  const homeStore = {
    lists,
    document,
    selectedTab,
    loading,
    pagination,
    isMobile,
    id
  }

  const homeStoreFunc = {
    onChangeTab,
    handlePaginationChange,
    postContractId,
    getQrBySignerId,
    deleteDocument,
    checkAndRedirectToContract,
    getDocumentLink,
    sendSms,
    getResignCount
  }

  return <HomeStoreContext.Provider value={{ homeStore, homeStoreFunc }}>{props.children}</HomeStoreContext.Provider>
}
export default withAuthStore(HomeStoreProvider)
